import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CalendarCheck,
  ChevronDown,
  ChevronUp,
  Euro,
  HeartPulse,
  Loader2,
  Minus,
  RefreshCw,
  Search,
  Shield,
  TrendingDown,
  TrendingUp,
  UserCircle,
  Users,
} from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { TrendBadge } from "../components/TrendBadge.js";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { type EventParticipation, type RiskLevel, api } from "../lib/api.js";
import { COCKPIT_EIGENE_KRANKHEITSTAGE as GRENZE, arbeitstageText } from "../lib/krankheitstage.js";

// ── Types ────────────────────────────────────────────────────────────────────

interface EmployeeBilling {
  total12m: number;
  total3m: number;
  avgMonthly: number;
  trend: string;
}

interface SickDayStats {
  sickDays12m: number;
  sickDays3m: number;
  sickEpisodes12m: number;
  lastSickDate: string | null;
}

interface LastReview {
  date: string;
  type: string;
  status: string;
  notes: string;
}

interface PLEmployee {
  id: number;
  displayName: string;
  department: string | null;
  position: string | null;
  hireDate: string | null;
  tenureMonths: number | null;
  retentionScore: number | null;
  riskLevel: RiskLevel | null;
  isConfirmedLeaving: boolean;
  topSignals: string[];
  billing: EmployeeBilling | null;
  events: EventParticipation[];
  eventAttendanceRate: number | null;
  sickDayStats: SickDayStats | null;
  lastReview: LastReview | null;
  lastDevelopmentTalk: string | null;
  talkOverdueRelevant?: boolean;
  trendPp?: number | null;
}

interface PLTeam {
  personalleiterName: string;
  careRole: "Personalleiter" | "HR Business Partner";
  teamSize: number;
  avgScore: number;
  highRiskCount: number;
  totalBilling12m: number;
  avgEventAttendance: number | null;
  avgSickDays12m: number | null;
  employees: PLEmployee[];
  trendPp?: number | null;
}

interface ManagerOption {
  name: string;
  role: "Personalleiter" | "HR Business Partner";
}

interface PLCockpitResponse {
  managers: ManagerOption[];
  teams: PLTeam[];
  requestId: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const REVIEW_TYPE_LABELS: Record<string, string> = {
  einzel_gespraech: "Einzelgespräch",
  gehaltsreview: "Gehaltsreview",
  entwicklungsgespraech: "Entwicklungsgespräch",
  versetzungsangebot: "Versetzungsangebot",
  sonstiges: "Sonstiges",
};

const REVIEW_STATUS_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  open: { color: "var(--risk-medium)", bg: "rgba(245,158,11,0.1)", label: "Offen" },
  in_progress: { color: "var(--accent)", bg: "rgba(234,91,11,0.1)", label: "Laufend" },
  done: { color: "var(--risk-low)", bg: "rgba(34,197,94,0.1)", label: "Abgeschlossen" },
};

function riskColor(level: RiskLevel | null): string {
  if (level === "high") return "var(--risk-high)";
  if (level === "medium") return "var(--risk-medium)";
  return "var(--risk-low)";
}

function trendIcon(trend: string) {
  if (trend === "up") return <TrendingUp size={10} style={{ color: "var(--risk-low)" }} />;
  if (trend === "down") return <TrendingDown size={10} style={{ color: "var(--risk-high)" }} />;
  return <Minus size={10} style={{ color: "var(--text-muted)" }} />;
}

// ── Sorting ──────────────────────────────────────────────────────────────────

type SortKey = "name" | "risk" | "billing" | "sick" | "events" | "tenure" | "review";
type SortDir = "asc" | "desc";

const DEFAULT_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  risk: "desc",
  billing: "desc",
  sick: "desc",
  events: "desc",
  tenure: "desc",
  review: "desc",
};

function cmpNum(a: number | null | undefined, b: number | null | undefined, dir: SortDir): number {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  return dir === "asc" ? (a as number) - (b as number) : (b as number) - (a as number);
}

function cmpStr(a: string | null | undefined, b: string | null | undefined, dir: SortDir): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return dir === "asc" ? a.localeCompare(b, "de") : b.localeCompare(a, "de");
}

function sortEmployees(list: PLEmployee[], key: SortKey, dir: SortDir): PLEmployee[] {
  return [...list].sort((a, b) => {
    switch (key) {
      case "name":
        return cmpStr(a.displayName, b.displayName, dir);
      // Bestätigte Kündigungen haben kein aussagekräftiges Risiko mehr → ans Ende
      case "risk":
        return cmpNum(
          a.isConfirmedLeaving ? null : a.retentionScore,
          b.isConfirmedLeaving ? null : b.retentionScore,
          dir,
        );
      case "billing":
        return cmpNum(a.billing?.total12m ?? null, b.billing?.total12m ?? null, dir);
      case "sick":
        return cmpNum(
          a.sickDayStats?.sickDays12m ?? null,
          b.sickDayStats?.sickDays12m ?? null,
          dir,
        );
      case "events":
        return cmpNum(a.eventAttendanceRate, b.eventAttendanceRate, dir);
      case "tenure":
        return cmpNum(a.tenureMonths, b.tenureMonths, dir);
      case "review":
        return cmpStr(
          a.lastDevelopmentTalk ?? a.lastReview?.date ?? null,
          b.lastDevelopmentTalk ?? b.lastReview?.date ?? null,
          dir,
        );
    }
  });
}

// ── Main Component ───────────────────────────────────────────────────────────

export function PersonalleiterCockpit() {
  const [selectedPL, setSelectedPL] = useState<string | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("risk");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(DEFAULT_DIR[key]);
    }
  }

  const queryKey = selectedPL !== null ? ["pl-cockpit", selectedPL] : ["pl-cockpit", "all"];

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<PLCockpitResponse>(
        selectedPL !== null
          ? `/employees/personalleiter-cockpit?personalleiter=${encodeURIComponent(selectedPL)}`
          : "/employees/personalleiter-cockpit",
      ),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const teams = data?.teams ?? [];
  const managers = data?.managers ?? [];

  // Filter by search term (manager name or employee name)
  const filteredTeams = useMemo(() => {
    if (!searchTerm.trim()) return teams;
    const q = searchTerm.toLowerCase();
    return teams.filter(
      (t) =>
        t.personalleiterName.toLowerCase().includes(q) ||
        t.employees.some((e) => e.displayName.toLowerCase().includes(q)),
    );
  }, [teams, searchTerm]);

  // Aggregate stats
  const totalEmployees = teams.reduce((s, t) => s + t.teamSize, 0);
  const totalHighRisk = teams.reduce((s, t) => s + t.highRiskCount, 0);
  const totalBilling = teams.reduce((s, t) => s + t.totalBilling12m, 0);
  const allScores = teams.filter((t) => t.avgScore > 0).map((t) => t.avgScore);
  const avgRisk =
    allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : 0;

  return (
    <AppLayout>
      <Header
        title="HR Cockpit"
        subtitle="Team-Übersicht pro Personalleiter & HR Business Partner · Wirtschaftlichkeit, Risiko, Engagement"
      />

      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* ── Controls: Manager Selector + Search ─────────────────────── */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 flex-1">
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 max-w-xs"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              <UserCircle size={14} style={{ color: "var(--accent)", flexShrink: 0 }} />
              <select
                value={selectedPL ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedPL(v || null);
                  setExpandedTeam(null);
                }}
                className="bg-transparent text-sm flex-1 outline-none cursor-pointer"
                style={{ color: "var(--text-primary)", border: "none" }}
              >
                <option value="">Alle Teams</option>
                {managers.map((m) => (
                  <option key={`${m.role}:${m.name}`} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg max-w-xs"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              <Search size={13} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Suche nach Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent text-sm outline-none w-44"
                style={{ color: "var(--text-primary)", border: "none" }}
              />
            </div>
          </div>

          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-opacity"
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
            }}
            onClick={() => void refetch()}
          >
            <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Lädt..." : "Aktualisieren"}
          </button>
        </div>

        {/* ── Summary Tiles ───────────────────────────────────────────── */}
        <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <SummaryTile
            icon={<Users size={14} />}
            label="Teams / Mitarbeiter"
            value={`${teams.length} / ${totalEmployees}`}
            color="var(--text-secondary)"
          />
          <SummaryTile
            icon={<AlertTriangle size={14} />}
            label="Hohes Risiko gesamt"
            value={String(totalHighRisk)}
            color="var(--risk-high)"
            sub={
              totalEmployees > 0
                ? `${Math.round((totalHighRisk / totalEmployees) * 100)}% aller Mitarbeiter`
                : undefined
            }
          />
          <SummaryTile
            icon={<Shield size={14} />}
            label="Ø Risiko-Score"
            value={pct(avgRisk)}
            color={
              avgRisk >= 0.6
                ? "var(--risk-high)"
                : avgRisk >= 0.4
                  ? "var(--risk-medium)"
                  : "var(--risk-low)"
            }
          />
          <SummaryTile
            icon={<Euro size={14} />}
            label="Gesamtumsatz 12M"
            value={fmt(totalBilling)}
            color="var(--accent-gold)"
          />
        </div>

        {/* ── Loading / Error ─────────────────────────────────────────── */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "rgba(234,91,11,0.1)", border: "1px solid rgba(234,91,11,0.2)" }}
            >
              <Loader2 size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
            </div>
            <p className="text-xs font-mono-data" style={{ color: "var(--text-muted)" }}>
              PERSONIO + SALESFORCE · LADE TEAMDATEN
            </p>
          </div>
        )}

        {isError && (
          <div
            className="flex items-center gap-3 p-4 rounded-lg mx-auto max-w-md"
            style={{ background: "var(--risk-high-bg)", border: "1px solid rgba(240,78,78,0.25)" }}
          >
            <AlertCircle size={15} style={{ color: "var(--risk-high)", flexShrink: 0 }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error instanceof Error ? error.message : "Fehler beim Laden"}
            </p>
          </div>
        )}

        {/* ── Team Cards ──────────────────────────────────────────────── */}
        {!isLoading && !isError && (
          <div className="flex flex-col gap-3">
            {filteredTeams.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  {searchTerm ? "Keine Ergebnisse für diese Suche." : "Keine Teamdaten vorhanden."}
                </p>
              </div>
            )}

            {filteredTeams.map((team, i) => {
              const teamKey = `${team.careRole}:${team.personalleiterName}`;
              const isExpanded = expandedTeam === teamKey;
              const hrRatio = team.teamSize > 0 ? team.highRiskCount / team.teamSize : 0;
              const threat = hrRatio >= 0.4 ? "critical" : hrRatio >= 0.2 ? "elevated" : "low";

              return (
                <div
                  key={teamKey}
                  className="card"
                  style={{
                    borderColor:
                      threat === "critical"
                        ? "rgba(240,78,78,0.35)"
                        : threat === "elevated"
                          ? "rgba(234,91,11,0.25)"
                          : "var(--border)",
                    animationDelay: `${i * 30}ms`,
                    opacity: 0,
                    animation: "plReveal 0.3s ease forwards",
                  }}
                >
                  {/* ── Team Header ──────────────────────────────────── */}
                  <div
                    className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none"
                    onClick={() => setExpandedTeam(isExpanded ? null : teamKey)}
                  >
                    {/* Manager avatar */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{
                        background: "var(--accent)",
                      }}
                    >
                      <span style={{ color: "#fff", fontSize: "11px", fontWeight: 700 }}>
                        {team.personalleiterName
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)}
                      </span>
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-semibold truncate"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {team.personalleiterName}
                      </div>
                      <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                        {team.teamSize} Mitarbeiter
                      </div>
                    </div>

                    {/* Quick stats */}
                    <div className="flex items-center gap-5 shrink-0">
                      <div className="flex items-center gap-1.5">
                        <QuickStat
                          label="Ø Risiko"
                          value={pct(team.avgScore)}
                          color={
                            team.avgScore >= 0.6
                              ? "var(--risk-high)"
                              : team.avgScore >= 0.4
                                ? "var(--risk-medium)"
                                : "var(--risk-low)"
                          }
                        />
                        <TrendBadge trendPp={team.trendPp} />
                      </div>
                      <QuickStat
                        label="Hoch-Risiko"
                        value={String(team.highRiskCount)}
                        color={team.highRiskCount > 0 ? "var(--risk-high)" : "var(--text-muted)"}
                      />
                      <QuickStat
                        label="Umsatz 12M"
                        value={team.totalBilling12m > 0 ? fmt(team.totalBilling12m) : "—"}
                        color="var(--accent-gold)"
                      />
                      <QuickStat
                        label="Ø Events"
                        value={
                          team.avgEventAttendance !== null ? pct(team.avgEventAttendance) : "—"
                        }
                        color="var(--text-secondary)"
                      />
                      <QuickStat
                        label="Ø eigene Krankheitstage"
                        value={
                          team.avgSickDays12m !== null
                            ? team.avgSickDays12m.toLocaleString("de-DE")
                            : "—"
                        }
                        color={
                          team.avgSickDays12m !== null && team.avgSickDays12m > GRENZE.teamRot
                            ? "var(--risk-high)"
                            : team.avgSickDays12m !== null &&
                                team.avgSickDays12m > GRENZE.teamOrange
                              ? "var(--risk-medium)"
                              : "var(--text-secondary)"
                        }
                      />
                    </div>

                    <div style={{ color: "var(--text-muted)" }}>
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </div>

                  {/* ── Expanded: Employee Table ─────────────────────── */}
                  {isExpanded && (
                    <div style={{ borderTop: "1px solid var(--border)" }}>
                      {/* Table header */}
                      <div
                        className="grid px-5 py-2.5 text-[9px] font-bold uppercase tracking-[0.12em]"
                        style={{
                          gridTemplateColumns:
                            "minmax(160px, 1.5fr) 130px 90px 110px 200px 80px 140px",
                          color: "var(--text-muted)",
                          borderBottom: "1px solid var(--border)",
                          background: "var(--bg-surface)",
                        }}
                      >
                        <SortHeader
                          label="Mitarbeiter"
                          sortKey="name"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Drop-Risiko"
                          sortKey="risk"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Umsatz 12M"
                          sortKey="billing"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Eigene Krankheitstage"
                          sortKey="sick"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Eventteilnahme (letzte 12)"
                          sortKey="events"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Zugehörigkeit"
                          sortKey="tenure"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                        <SortHeader
                          label="Letztes Gespräch"
                          sortKey="review"
                          activeKey={sortKey}
                          dir={sortDir}
                          onSort={handleSort}
                        />
                      </div>

                      {/* Employee rows */}
                      {sortEmployees(team.employees, sortKey, sortDir).map((emp) => (
                        <EmployeeRow key={emp.id} employee={emp} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes plReveal {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        select option {
          background: var(--bg-card);
          color: var(--text-primary);
        }
      `}</style>
    </AppLayout>
  );
}

// ── Sub-Components ───────────────────────────────────────────────────────────

function SummaryTile({
  icon,
  label,
  value,
  color,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
  sub?: string;
}) {
  return (
    <div
      className="card px-4 py-3 flex items-center gap-3"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <div
        className="w-8 h-8 rounded flex items-center justify-center"
        style={{ background: `${color}14`, color, flexShrink: 0 }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div
          className="text-[9px] uppercase tracking-[0.12em] font-semibold mb-0.5"
          style={{ color: "var(--text-muted)" }}
        >
          {label}
        </div>
        <div className="text-lg font-bold font-mono-data leading-none" style={{ color }}>
          {value}
        </div>
        {sub && (
          <div className="text-[9px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  sortKey: k,
  activeKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = k === activeKey;
  return (
    <button
      type="button"
      className="flex items-center gap-1 text-left select-none"
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: active ? "var(--accent)" : "var(--text-muted)",
        font: "inherit",
        letterSpacing: "inherit",
        textTransform: "inherit",
      }}
      onClick={() => onSort(k)}
    >
      <span>{label}</span>
      <span style={{ display: "inline-flex", width: 10, height: 10, opacity: active ? 1 : 0.25 }}>
        {active ? (
          dir === "asc" ? (
            <ChevronUp size={10} />
          ) : (
            <ChevronDown size={10} />
          )
        ) : (
          <ChevronDown size={10} />
        )}
      </span>
    </button>
  );
}

function QuickStat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="text-center" style={{ minWidth: 60 }}>
      <div
        className="text-[8px] uppercase tracking-[0.1em] font-semibold mb-0.5"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </div>
      <div className="text-xs font-bold font-mono-data" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function EmployeeRow({ employee: emp }: { employee: PLEmployee }) {
  const scoreColor = riskColor(emp.riskLevel);
  const sickColor = emp.sickDayStats
    ? emp.sickDayStats.sickDays12m > GRENZE.personRot
      ? "var(--risk-high)"
      : emp.sickDayStats.sickDays12m > GRENZE.personOrange
        ? "var(--risk-medium)"
        : "var(--text-secondary)"
    : "var(--text-muted)";

  const reviewMeta = emp.lastReview
    ? (REVIEW_STATUS_COLORS[emp.lastReview.status] ?? REVIEW_STATUS_COLORS.open)
    : null;

  return (
    <div
      className="grid items-center px-5 py-3 transition-colors"
      style={{
        gridTemplateColumns: "minmax(160px, 1.5fr) 130px 90px 110px 200px 80px 140px",
        borderBottom: "1px solid var(--border)",
        background: emp.isConfirmedLeaving ? "rgba(240,78,78,0.04)" : "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = emp.isConfirmedLeaving
          ? "rgba(240,78,78,0.07)"
          : "var(--bg-hover)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = emp.isConfirmedLeaving
          ? "rgba(240,78,78,0.04)"
          : "transparent";
      }}
    >
      {/* Name + Position */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          {emp.isConfirmedLeaving && (
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{
                background: "var(--risk-high)",
                animation: "dotPulse 1.4s ease-in-out infinite",
              }}
            />
          )}
          <span className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>
            {emp.displayName}
          </span>
        </div>
        <div className="text-[9px] truncate" style={{ color: "var(--text-muted)" }}>
          {emp.position ?? emp.department ?? "—"}
        </div>
      </div>

      {/* Drop-Risiko — bei bestätigter Kündigung obsolet, stattdessen Status zeigen */}
      {emp.isConfirmedLeaving ? (
        <div>
          <span
            className="px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap"
            style={{ background: "rgba(240,78,78,0.1)", color: "var(--risk-high)" }}
            title="Kündigung liegt vor — das Drop-Risiko ist nicht mehr aussagekräftig"
          >
            Austritt fix
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <div
            className="h-1.5 rounded-full"
            style={{
              width: `${(emp.retentionScore ?? 0) * 100}%`,
              maxWidth: 40,
              minWidth: 4,
              background: scoreColor,
            }}
          />
          <span className="text-[11px] font-mono-data font-bold" style={{ color: scoreColor }}>
            {emp.retentionScore !== null ? pct(emp.retentionScore) : "—"}
          </span>
          <TrendBadge trendPp={emp.trendPp} size={10} />
        </div>
      )}

      {/* Umsatz 12M */}
      <div>
        {emp.billing ? (
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-mono-data" style={{ color: "var(--accent-gold)" }}>
              {fmt(emp.billing.total12m)}
            </span>
            {trendIcon(emp.billing.trend)}
          </div>
        ) : (
          <span className="text-[11px] font-mono-data" style={{ color: "var(--text-muted)" }}>
            —
          </span>
        )}
      </div>

      {/* Eigene Krankheitstage (Arbeitstage, ohne „Kind krank") */}
      <div>
        {emp.sickDayStats ? (
          <div>
            <div className="flex items-center gap-1">
              <HeartPulse size={10} style={{ color: sickColor }} />
              <span
                className="text-[11px] font-mono-data font-semibold"
                style={{ color: sickColor }}
              >
                {arbeitstageText(emp.sickDayStats.sickDays12m)}
              </span>
              <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>
                / 12M
              </span>
            </div>
            <div className="text-[9px]" style={{ color: "var(--text-muted)" }}>
              {emp.sickDayStats.sickEpisodes12m} Krankmeldungen ·{" "}
              {arbeitstageText(emp.sickDayStats.sickDays3m)} letzte 3M
            </div>
          </div>
        ) : (
          <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            —
          </span>
        )}
      </div>

      {/* Event-Teilnahme (letzte 12) */}
      <div className="flex items-center gap-0.5">
        {emp.events.some((e) => e.explicit) ? (
          <>
            {/* W3: nur gepflegte Quartale einfärben; leere (nicht explizit) bleiben
                neutral — sonst zeigt die Cockpit-Liste falschen "Rückzug" und
                widerspricht dem Detail-Fenster. */}
            {emp.events.map((ev, i) => (
              <div
                key={i}
                title={`${ev.label}: ${ev.explicit ? (ev.attended ? "Ja" : "Nein") : "nicht gepflegt"}`}
                className="rounded-sm"
                style={{
                  width: 14,
                  height: 14,
                  background: !ev.explicit
                    ? "var(--border)"
                    : ev.attended
                      ? "var(--risk-low)"
                      : "var(--risk-high)",
                  opacity: ev.explicit ? 0.85 : 0.4,
                }}
              />
            ))}
            <span
              className="text-[9px] font-mono-data ml-1.5"
              style={{
                color:
                  emp.eventAttendanceRate == null
                    ? "var(--text-muted)"
                    : emp.eventAttendanceRate < 0.3
                      ? "var(--risk-high)"
                      : emp.eventAttendanceRate < 0.6
                        ? "var(--risk-medium)"
                        : "var(--risk-low)",
              }}
            >
              {emp.eventAttendanceRate !== null ? pct(emp.eventAttendanceRate) : ""}
            </span>
          </>
        ) : (
          <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
            Keine Daten
          </span>
        )}
      </div>

      {/* Tenure */}
      <div>
        <span className="text-[11px] font-mono-data" style={{ color: "var(--text-secondary)" }}>
          {emp.tenureMonths !== null
            ? emp.tenureMonths >= 12
              ? `${Math.floor(emp.tenureMonths / 12)}J ${emp.tenureMonths % 12}M`
              : `${emp.tenureMonths}M`
            : "—"}
        </span>
      </div>

      {/* Letztes Gespräch — primär das Personio-Feld "Letztes Gespräch"
          (Entwicklungsgespräch), rot wenn > 6 Monate her; Fallback: tool-interner Eintrag */}
      <div>
        {emp.lastDevelopmentTalk ? (
          (() => {
            const monthsAgo =
              (Date.now() - new Date(emp.lastDevelopmentTalk).getTime()) /
              (1000 * 60 * 60 * 24 * 30);
            const isOld = monthsAgo > 6;
            // Überfällig nur, wo der Gesprächszyklus gilt (CU-Team, nicht ausgesetzt)
            const overdue = (emp.talkOverdueRelevant ?? true) && isOld;
            // Grün NUR bei wirklich aktuellem Gespräch (≤6 Monate). Ist das Gespräch
            // alt, der Zyklus aber ausgesetzt/nicht anwendbar → neutral grau (nicht
            // grün, sonst wirkt ein 13 Monate altes Gespräch fälschlich „aktuell").
            const color = overdue
              ? "var(--risk-high)"
              : isOld
                ? "var(--text-muted)"
                : "var(--risk-low)";
            return (
              <div>
                <div className="flex items-center gap-1.5">
                  <CalendarCheck size={10} style={{ color }} />
                  <span className="text-[10px] font-medium" style={{ color }}>
                    {fmtDate(emp.lastDevelopmentTalk)}
                  </span>
                </div>
                {overdue && (
                  <span
                    className="text-[8px] px-1 py-px rounded mt-0.5 inline-block"
                    style={{ background: "rgba(240,78,78,0.1)", color: "var(--risk-high)" }}
                  >
                    überfällig
                  </span>
                )}
              </div>
            );
          })()
        ) : emp.lastReview && reviewMeta ? (
          <div>
            <div className="flex items-center gap-1.5">
              <CalendarCheck size={10} style={{ color: reviewMeta.color }} />
              <span className="text-[10px] font-medium" style={{ color: reviewMeta.color }}>
                {fmtDate(emp.lastReview.date)}
              </span>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span
                className="text-[8px] px-1 py-px rounded"
                style={{ background: reviewMeta.bg, color: reviewMeta.color }}
              >
                {reviewMeta.label}
              </span>
              <span
                className="text-[8px] truncate"
                style={{ color: "var(--text-muted)", maxWidth: 80 }}
              >
                {REVIEW_TYPE_LABELS[emp.lastReview.type] ?? emp.lastReview.type}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Calendar size={10} style={{ color: "var(--risk-medium)" }} />
            <span className="text-[9px] font-semibold" style={{ color: "var(--risk-medium)" }}>
              Kein Eintrag
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
